/**
 * WebGL2 renderer. Stage 1 scope:
 *   - Bg + static rink (rim/lines/pockets) reused from the Canvas-2D
 *     static cache, uploaded as a texture and drawn as a full-screen quad.
 *   - Grid with GPU-side distortion (force points as uniforms; vertex
 *     shader applies the wave).
 *   - Paddles + puck as instanced SDF circles.
 *
 * Out of scope for this stage (still Canvas-2D-only): particles, wall
 * flashes, impact rings, goal celebration, score, banner. To use this
 * renderer, append `?gl=1` to the URL.
 */

import * as twgl from 'twgl.js';
import { RINK, type PhysicsEvent, type Role } from '../types';
import type { GameState } from './physics';
import {
  applyEvents as applyEventsCanvas,
  getStaticLayer,
  getGridForceSnapshot,
  getEffectsSnapshot,
  getShakeOffset,
  getCurrentConfig,
  getCurrentPuckSpeed,
  tickScene,
  type Effect,
} from './render';

// ─── Re-exports so the router has a uniform shape ─────────────────────────

/** Forward events to the Canvas-2D module so its state (effects, grid
 *  forces, sparks) gets updated. We read that state for rendering. */
export const applyEvents = applyEventsCanvas;

export function fitCanvas(canvas: HTMLCanvasElement): void {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = Math.round(rect.width * dpr);
  canvas.height = Math.round(rect.height * dpr);
}

export function warmStaticCache(canvas: HTMLCanvasElement, role: Role): void {
  // Pre-build the offscreen canvas the texture will sample from.
  getStaticLayer(canvas, role);
}

// ─── Projection (mirrors render.ts; shared via duplication for now) ───────

const ENTITY_FORESHORTEN = 1.0;

interface View {
  canvasW: number;
  canvasH: number;
  nearY: number;
  farY: number;
  nearHalfWidth: number;
  farHalfWidth: number;
  role: Role;
  // 1-point perspective: screen_y = horizonY + screenYScale/(depth+a),
  // halfHere = nearHalfWidth * a/(depth+a). See render.ts for derivation.
  a: number;
  horizonY: number;
  screenYScale: number;
}

/** Max CSS-pixel width the rink occupies on screen — see render.ts. */
const RINK_MAX_CSS_PX = 1100;

function computeView(canvas: HTMLCanvasElement, role: Role): View {
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  const rinkW = Math.min(canvas.width, RINK_MAX_CSS_PX * dpr);
  const nearY = canvas.height * 0.93;
  const farY = canvas.height * 0.18;
  const nearHalfWidth = rinkW * 0.46;
  const farHalfWidth = rinkW * 0.16;
  const a = RINK.width * farHalfWidth / (nearHalfWidth - farHalfWidth);
  const dN = (nearY - farY) * (RINK.width + a) / RINK.width;
  const horizonY = nearY - dN;
  const screenYScale = a * dN;
  return {
    canvasW: canvas.width,
    canvasH: canvas.height,
    nearY, farY, nearHalfWidth, farHalfWidth,
    role,
    a, horizonY, screenYScale,
  };
}

function project(wx: number, wy: number, v: View): { x: number; y: number; latScale: number } {
  const depth = v.role === 'host' ? wx : RINK.width - wx;
  const invZ = 1 / (depth + v.a);
  const screenY = v.horizonY + v.screenYScale * invZ;
  const halfHere = v.nearHalfWidth * v.a * invZ;
  const wyAdj = v.role === 'host' ? wy : RINK.height - wy;
  const lateralT = (wyAdj - RINK.height / 2) / (RINK.height / 2);
  const screenX = v.canvasW / 2 + lateralT * halfHere;
  const latScale = halfHere / (RINK.height / 2);
  return { x: screenX, y: screenY, latScale };
}

export function screenToRink(
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number,
  role: Role,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const dpr = canvas.width / rect.width;
  const sx = (clientX - rect.left) * dpr;
  const sy = (clientY - rect.top) * dpr;
  const v = computeView(canvas, role);
  const dy = sy - v.horizonY;
  let depth: number;
  if (dy <= 0) {
    depth = RINK.width;
  } else {
    depth = v.screenYScale / dy - v.a;
    if (depth < 0) depth = 0;
    if (depth > RINK.width) depth = RINK.width;
  }
  const invZ = 1 / (depth + v.a);
  const halfHere = v.nearHalfWidth * v.a * invZ;
  const lateralT = (sx - v.canvasW / 2) / halfHere;
  const wyAdj = RINK.height / 2 + lateralT * (RINK.height / 2);
  const wy = role === 'host' ? wyAdj : RINK.height - wyAdj;
  const wx = role === 'host' ? depth : RINK.width - depth;
  return { x: wx, y: wy };
}

// ─── Color helpers ────────────────────────────────────────────────────────

// Mutable so a palette change at startup can swap them. Initial values
// are the Synthwave defaults.
const PALETTE = {
  host: '#ff2bd6',
  guest: '#22e7ff',
  puck: '#fff7a8',
  bg: '#08020e',
};

export function applyRenderPaletteGL(host: string, guest: string, puck: string, bg: string): void {
  PALETTE.host = host;
  PALETTE.guest = guest;
  PALETTE.puck = puck;
  PALETTE.bg = bg;
}

function hexToRgba(hex: string): [number, number, number, number] {
  const m = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i);
  if (!m) throw new Error(`bad hex: ${hex}`);
  return [
    parseInt(m[1]!, 16) / 255,
    parseInt(m[2]!, 16) / 255,
    parseInt(m[3]!, 16) / 255,
    m[4] ? parseInt(m[4]!, 16) / 255 : 1.0,
  ];
}

// ─── Shaders ──────────────────────────────────────────────────────────────

const fullQuadVS = `#version 300 es
in vec2 position;
out vec2 v_uv;
void main() {
  // Flip Y because Canvas Y is top-down but NDC Y is bottom-up.
  v_uv = vec2(position.x * 0.5 + 0.5, 1.0 - (position.y * 0.5 + 0.5));
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const fullQuadFS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_tex;
out vec4 outColor;
void main() {
  outColor = texture(u_tex, v_uv);
}`;

const MAX_FORCES = 16;

const gridVS = `#version 300 es
precision highp float;
in vec2 a_worldPos;

uniform vec2 u_resolution;
uniform vec2 u_shake;
uniform float u_rinkW;
uniform float u_rinkH;
uniform float u_role; // 0 = host, 1 = guest
uniform vec4 u_view; // nearY, farY, nearHalfWidth, farHalfWidth
uniform vec3 u_persp; // a, horizonY, screenYScale (1-point perspective)

uniform int u_forceCount;
// Each force: (wx, wy, magnitude * ageFalloff, radius)
uniform vec4 u_forces[${MAX_FORCES}];
// Phase term: cos(dist/24 - age * 3π). We precompute ageTerm = age * 3π on CPU.
uniform float u_forceAgeTerm[${MAX_FORCES}];

vec2 projectWorld(vec2 w) {
  float depth = u_role < 0.5 ? w.x : u_rinkW - w.x;
  float invZ = 1.0 / (depth + u_persp.x);
  float screenY = u_persp.y + u_persp.z * invZ;
  float halfHere = u_view.z * u_persp.x * invZ;
  float wyAdj = u_role < 0.5 ? w.y : u_rinkH - w.y;
  float lateralT = (wyAdj - u_rinkH * 0.5) / (u_rinkH * 0.5);
  float screenX = u_resolution.x * 0.5 + lateralT * halfHere;
  return vec2(screenX, screenY);
}

void main() {
  vec2 wp = a_worldPos;
  for (int i = 0; i < ${MAX_FORCES}; i++) {
    if (i >= u_forceCount) break;
    vec4 f = u_forces[i];
    vec2 d = wp - f.xy;
    float dist = length(d);
    if (dist < 0.001 || dist > f.w) continue;
    float distFalloff = 1.0 - dist / f.w;
    float wave = cos(dist / 24.0 - u_forceAgeTerm[i]);
    float force = f.z * distFalloff * wave;
    wp += (d / dist) * force;
  }
  vec2 screen = projectWorld(wp) + u_shake;
  vec2 ndc = (screen / u_resolution) * 2.0 - 1.0;
  ndc.y = -ndc.y;
  gl_Position = vec4(ndc, 0.0, 1.0);
}`;

const gridFS = `#version 300 es
precision highp float;
uniform vec4 u_color;
out vec4 outColor;
void main() {
  outColor = u_color;
}`;

// Entity (paddles, puck) as instanced quads. Each instance has a screen
// position, halo radius (in screen px), Y aspect, color. Fragment uses
// the quad coord as a signed distance from center to draw a body +
// halo with smooth edge.
const entityVS = `#version 300 es
precision highp float;
in vec2 a_quadCorner;        // [-1, 1] quad corners
in vec3 a_instancePosSize;   // x, y, halo radius (screen px)
in vec2 a_instanceAspect;    // (1, foreshorten) — y stretch
in vec4 a_instanceColor;
in float a_instanceBodyFrac; // body radius / halo radius (e.g. 0.5)
in float a_instanceShape;    // 0 = circle, 1 = rectangle

uniform vec2 u_resolution;
uniform vec2 u_shake;

out vec2 v_quadCoord;
out vec4 v_color;
out float v_bodyFrac;
out float v_shape;

void main() {
  v_quadCoord = a_quadCorner;
  v_color = a_instanceColor;
  v_bodyFrac = a_instanceBodyFrac;
  v_shape = a_instanceShape;
  vec2 screen = a_instancePosSize.xy
              + a_quadCorner * a_instancePosSize.z * a_instanceAspect
              + u_shake;
  vec2 ndc = (screen / u_resolution) * 2.0 - 1.0;
  ndc.y = -ndc.y;
  gl_Position = vec4(ndc, 0.0, 1.0);
}`;

const entityFS = `#version 300 es
precision highp float;
in vec2 v_quadCoord;
in vec4 v_color;
in float v_bodyFrac;
in float v_shape;
out vec4 outColor;

void main() {
  // SDF: 0 at center, 1 at edge. Circle = Euclidean length; rectangle
  // = Chebyshev (max of |x|, |y|). Single branch on shape.
  float d = (v_shape > 0.5)
    ? max(abs(v_quadCoord.x), abs(v_quadCoord.y))
    : length(v_quadCoord);
  if (d > 1.0) discard;
  // Solid body up to bodyFrac, then a halo that fades to 0 at edge.
  float bodyA = 1.0 - smoothstep(v_bodyFrac, v_bodyFrac + 0.05, d);
  float haloA = (1.0 - smoothstep(v_bodyFrac, 1.0, d)) * 0.45;
  float a = clamp(bodyA + haloA * (1.0 - bodyA), 0.0, 1.0);
  outColor = vec4(v_color.rgb, v_color.a * a);
}`;

// Streaks (particles). Per-instance: head + tail screen positions, width,
// color, age01. Vertex shader builds a quad oriented along head→tail.
// Fragment fades by quad distance + age. Drawn with additive blending.
const particleVS = `#version 300 es
precision highp float;
in vec2 a_quadCorner;        // [-1, 1]
in vec2 a_head;              // screen px
in vec2 a_tail;              // screen px
in float a_width;            // perpendicular half-thickness
in vec4 a_color;
in float a_age01;
uniform vec2 u_resolution;
uniform vec2 u_shake;
out vec2 v_quadCoord;
out vec4 v_color;
out float v_age01;
void main() {
  v_quadCoord = a_quadCorner;
  v_color = a_color;
  v_age01 = a_age01;
  vec2 center = (a_head + a_tail) * 0.5;
  vec2 along = (a_head - a_tail) * 0.5;       // half-length vector
  vec2 dir = length(along) < 0.5 ? vec2(1.0, 0.0) : normalize(along);
  vec2 perp = vec2(-dir.y, dir.x) * a_width;
  vec2 screen = center + a_quadCorner.x * along + a_quadCorner.y * perp + u_shake;
  vec2 ndc = (screen / u_resolution) * 2.0 - 1.0;
  ndc.y = -ndc.y;
  gl_Position = vec4(ndc, 0.0, 1.0);
}`;

const particleFS = `#version 300 es
precision highp float;
in vec2 v_quadCoord;
in vec4 v_color;
in float v_age01;
out vec4 outColor;
void main() {
  float d = length(v_quadCoord);
  if (d > 1.0) discard;
  // Bright center, fade to edge.
  float intensity = (1.0 - d * d) * (1.0 - v_age01);
  outColor = vec4(v_color.rgb * intensity, v_color.a * intensity);
}`;

// "Splat" — instanced circular sprite. type=0 fills, type=1 draws a ring.
// Used for wall flashes (filled) and impact rings (ring). Additive.
const splatVS = `#version 300 es
precision highp float;
in vec2 a_quadCorner;
in vec2 a_screen;
in vec2 a_radius;     // x = halo radius, y = aspect (foreshortening)
in vec4 a_color;
in float a_age01;
in float a_type;      // 0 = filled blob, 1 = ring
uniform vec2 u_resolution;
uniform vec2 u_shake;
out vec2 v_quadCoord;
out vec4 v_color;
out float v_age01;
out float v_type;
void main() {
  v_quadCoord = a_quadCorner;
  v_color = a_color;
  v_age01 = a_age01;
  v_type = a_type;
  vec2 screen = a_screen + a_quadCorner * a_radius + u_shake;
  vec2 ndc = (screen / u_resolution) * 2.0 - 1.0;
  ndc.y = -ndc.y;
  gl_Position = vec4(ndc, 0.0, 1.0);
}`;

const splatFS = `#version 300 es
precision highp float;
in vec2 v_quadCoord;
in vec4 v_color;
in float v_age01;
in float v_type;
out vec4 outColor;
void main() {
  float d = length(v_quadCoord);
  if (d > 1.0) discard;
  float intensity;
  if (v_type < 0.5) {
    // Filled blob: bright center fading to edge.
    intensity = (1.0 - d * d);
  } else {
    // Ring at ~0.85 of radius, narrow band.
    intensity = smoothstep(0.55, 0.85, d) * (1.0 - smoothstep(0.85, 1.0, d));
    intensity *= 2.0;
  }
  intensity *= (1.0 - v_age01);
  if (intensity < 0.01) discard;
  outColor = vec4(v_color.rgb * intensity, v_color.a * intensity);
}`;

// Starfield. Pre-generated star positions in NDC space; vertex shader
// places quads, fragment draws a soft point sprite. Twinkle phase per
// star + slow downward scroll over time.
const starVS = `#version 300 es
precision highp float;
in vec2 a_quadCorner;
in vec3 a_starPos;          // NDC.xy + size (px)
in vec4 a_starColor;
in float a_phase;
uniform vec2 u_resolution;
uniform float u_time;       // seconds
out vec2 v_quadCoord;
out vec4 v_color;
out float v_intensity;
void main() {
  v_quadCoord = a_quadCorner;
  v_color = a_starColor;
  v_intensity = 0.35 + 0.55 * (0.5 + 0.5 * sin(u_time * 1.8 + a_phase));
  // Slow downward scroll in NDC. Wrap with mod.
  float yShift = mod(u_time * 0.03, 2.0);
  vec2 ndcStar = vec2(a_starPos.x, mod(a_starPos.y - yShift + 1.0, 2.0) - 1.0);
  vec2 screen = (ndcStar * 0.5 + 0.5) * u_resolution;
  screen.y = u_resolution.y - screen.y; // flip so positive Y = down
  vec2 quad = screen + a_quadCorner * a_starPos.z;
  vec2 ndc = (quad / u_resolution) * 2.0 - 1.0;
  ndc.y = -ndc.y;
  gl_Position = vec4(ndc, 0.0, 1.0);
}`;

const starFS = `#version 300 es
precision highp float;
in vec2 v_quadCoord;
in vec4 v_color;
in float v_intensity;
out vec4 outColor;
void main() {
  float d = length(v_quadCoord);
  if (d > 1.0) discard;
  // Sharp bright center, soft falloff.
  float intensity = pow(1.0 - d, 2.0) * v_intensity;
  outColor = vec4(v_color.rgb * intensity, intensity);
}`;

// Solid colored geometry (positions in screen px). Used for the goal
// celebration trapezoid. Normal alpha blending.
const solidVS = `#version 300 es
precision highp float;
in vec2 a_position;
uniform vec2 u_resolution;
uniform vec2 u_shake;
void main() {
  vec2 screen = a_position + u_shake;
  vec2 ndc = (screen / u_resolution) * 2.0 - 1.0;
  ndc.y = -ndc.y;
  gl_Position = vec4(ndc, 0.0, 1.0);
}`;

const solidFS = `#version 300 es
precision highp float;
uniform vec4 u_color;
out vec4 outColor;
void main() { outColor = u_color; }`;

// ─── Grid geometry ────────────────────────────────────────────────────────

const GRID_COLS = 16;
const GRID_ROWS = 10;
const GRID_VERT_COUNT = (GRID_COLS + 1) * (GRID_ROWS + 1);
// World-unit inset so the grid sits comfortably inside the rim's
// rounded corners (no grid lines spilling into the corner cut-offs).
const GRID_MARGIN = 30;

/** Build the line-index list once: pairs of indices into a flat
 *  GRID_VERT_COUNT array of world positions. */
function buildGridGeometry(): { positions: Float32Array; indices: Uint16Array } {
  const positions = new Float32Array(GRID_VERT_COUNT * 2);
  const indices: number[] = [];
  const stride = GRID_COLS + 1;
  const innerW = RINK.width - 2 * GRID_MARGIN;
  const innerH = RINK.height - 2 * GRID_MARGIN;
  for (let j = 0; j <= GRID_ROWS; j++) {
    const wy = GRID_MARGIN + (j / GRID_ROWS) * innerH;
    for (let i = 0; i <= GRID_COLS; i++) {
      const wx = GRID_MARGIN + (i / GRID_COLS) * innerW;
      const idx = j * stride + i;
      positions[idx * 2] = wx;
      positions[idx * 2 + 1] = wy;
    }
  }
  // Horizontal lines: pairs along each row.
  for (let j = 0; j <= GRID_ROWS; j++) {
    for (let i = 0; i < GRID_COLS; i++) {
      const a = j * stride + i;
      const b = a + 1;
      indices.push(a, b);
    }
  }
  // Vertical lines: pairs along each column.
  for (let i = 0; i <= GRID_COLS; i++) {
    for (let j = 0; j < GRID_ROWS; j++) {
      const a = j * stride + i;
      const b = a + stride;
      indices.push(a, b);
    }
  }
  return { positions, indices: new Uint16Array(indices) };
}

// ─── GL resource state ────────────────────────────────────────────────────

interface GLState {
  gl: WebGL2RenderingContext;
  fullQuadProg: twgl.ProgramInfo;
  gridProg: twgl.ProgramInfo;
  entityProg: twgl.ProgramInfo;
  particleProg: twgl.ProgramInfo;
  splatProg: twgl.ProgramInfo;
  solidProg: twgl.ProgramInfo;
  starProg: twgl.ProgramInfo;
  fullQuadBuf: twgl.BufferInfo;
  gridBuf: twgl.BufferInfo;
  entityBuf: twgl.BufferInfo;
  entityInstanceBuf: WebGLBuffer;
  entityVAO: WebGLVertexArrayObject;
  particleInstanceBuf: WebGLBuffer;
  particleVAO: WebGLVertexArrayObject;
  splatInstanceBuf: WebGLBuffer;
  splatVAO: WebGLVertexArrayObject;
  starInstanceBuf: WebGLBuffer;
  starVAO: WebGLVertexArrayObject;
  /** Number of stars to render. */
  starCount: number;
  /** Reusable dynamic buffer for the goal-celebration trapezoid. */
  solidBuf: WebGLBuffer;
  staticTex: WebGLTexture;
  staticTexKey: string;
}
let glState: GLState | null = null;

function initGL(canvas: HTMLCanvasElement): GLState {
  const gl = canvas.getContext('webgl2', {
    antialias: true,
    alpha: false,
    premultipliedAlpha: false,
  });
  if (!gl) throw new Error('WebGL2 not supported by this browser');

  const fullQuadProg = twgl.createProgramInfo(gl, [fullQuadVS, fullQuadFS]);
  const gridProg = twgl.createProgramInfo(gl, [gridVS, gridFS]);
  const entityProg = twgl.createProgramInfo(gl, [entityVS, entityFS]);
  const particleProg = twgl.createProgramInfo(gl, [particleVS, particleFS]);
  const splatProg = twgl.createProgramInfo(gl, [splatVS, splatFS]);
  const solidProg = twgl.createProgramInfo(gl, [solidVS, solidFS]);
  const starProg = twgl.createProgramInfo(gl, [starVS, starFS]);

  const fullQuadBuf = twgl.createBufferInfoFromArrays(gl, {
    position: { numComponents: 2, data: [-1, -1, 1, -1, -1, 1, 1, 1] },
    indices: [0, 1, 2, 1, 3, 2],
  });

  const grid = buildGridGeometry();
  const gridBuf = twgl.createBufferInfoFromArrays(gl, {
    a_worldPos: { numComponents: 2, data: grid.positions },
    indices: grid.indices,
  });

  // Entity quad (shared geometry for all instances).
  const entityBuf = twgl.createBufferInfoFromArrays(gl, {
    a_quadCorner: { numComponents: 2, data: [-1, -1, 1, -1, -1, 1, 1, 1] },
    indices: [0, 1, 2, 1, 3, 2],
  });

  // Instance data buffer (filled per frame). 3 entities × per-instance attrs.
  // Layout per instance: posSize(3) + aspect(2) + color(4) + bodyFrac(1) = 10 floats.
  const entityInstanceBuf = gl.createBuffer();
  if (!entityInstanceBuf) throw new Error('failed to create instance buffer');

  // Build a VAO that ties entity geometry + instance attribs together.
  const entityVAO = gl.createVertexArray();
  if (!entityVAO) throw new Error('failed to create VAO');
  gl.bindVertexArray(entityVAO);
  twgl.setBuffersAndAttributes(gl, entityProg, entityBuf);
  setupEntityInstanceAttribs(gl, entityProg, entityInstanceBuf);
  gl.bindVertexArray(null);

  // Static texture (uploaded later from offscreen canvas).
  const staticTex = gl.createTexture();
  if (!staticTex) throw new Error('failed to create texture');
  gl.bindTexture(gl.TEXTURE_2D, staticTex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  // Particle (streak) instance buffer + VAO. Quad geometry shared with
  // entity quad layout: -1..1 in both axes.
  const particleQuad = twgl.createBufferInfoFromArrays(gl, {
    a_quadCorner: { numComponents: 2, data: [-1, -1, 1, -1, -1, 1, 1, 1] },
    indices: [0, 1, 2, 1, 3, 2],
  });
  const particleInstanceBuf = gl.createBuffer();
  if (!particleInstanceBuf) throw new Error('failed to create particle instance buffer');
  const particleVAO = gl.createVertexArray();
  if (!particleVAO) throw new Error('failed to create particle VAO');
  gl.bindVertexArray(particleVAO);
  twgl.setBuffersAndAttributes(gl, particleProg, particleQuad);
  setupParticleInstanceAttribs(gl, particleProg, particleInstanceBuf);
  gl.bindVertexArray(null);

  // Splat (rings + flashes) instance buffer + VAO.
  const splatInstanceBuf = gl.createBuffer();
  if (!splatInstanceBuf) throw new Error('failed to create splat instance buffer');
  const splatVAO = gl.createVertexArray();
  if (!splatVAO) throw new Error('failed to create splat VAO');
  gl.bindVertexArray(splatVAO);
  twgl.setBuffersAndAttributes(gl, splatProg, particleQuad); // same quad geometry
  setupSplatInstanceAttribs(gl, splatProg, splatInstanceBuf);
  gl.bindVertexArray(null);

  // Solid-color quad buffer (positions in screen px, refreshed per draw).
  const solidBuf = gl.createBuffer();
  if (!solidBuf) throw new Error('failed to create solid buffer');

  // Starfield: pre-generate fixed positions. Three populations:
  //   - 80% small "dust" (size 1.0-2.4) — the base shimmer.
  //   - 15% medium tinted stars (2.4-4.0) — visible color flecks.
  //   - 5%  large "feature" stars (4.0-6.5) — bright pop points.
  // Tints: white-warm / pink / cyan / gold / violet to read as a
  // proper nebula background, not just specks.
  const STAR_COUNT = 720;
  const starData = new Float32Array(STAR_COUNT * STAR_INSTANCE_STRIDE_F);
  for (let i = 0; i < STAR_COUNT; i++) {
    const o = i * STAR_INSTANCE_STRIDE_F;
    starData[o + 0] = Math.random() * 2 - 1;            // ndc.x
    starData[o + 1] = Math.random() * 2 - 1;            // ndc.y

    // Size tier — small dust dominates, with a long tail of bigger stars.
    const sizeRoll = Math.random();
    let size: number;
    if (sizeRoll < 0.80) size = 1.0 + Math.random() * 1.4;
    else if (sizeRoll < 0.95) size = 2.4 + Math.random() * 1.6;
    else size = 4.0 + Math.random() * 2.5;
    starData[o + 2] = size;

    // Tint: weighted toward warm white with colored accents.
    const tint = Math.random();
    if (tint < 0.55) {
      starData[o + 3] = 1.0; starData[o + 4] = 0.96; starData[o + 5] = 0.92; // warm white
    } else if (tint < 0.72) {
      starData[o + 3] = 1.0; starData[o + 4] = 0.40; starData[o + 5] = 0.85; // pink
    } else if (tint < 0.85) {
      starData[o + 3] = 0.40; starData[o + 4] = 0.90; starData[o + 5] = 1.0; // cyan
    } else if (tint < 0.93) {
      starData[o + 3] = 1.0; starData[o + 4] = 0.85; starData[o + 5] = 0.45; // gold
    } else {
      starData[o + 3] = 0.75; starData[o + 4] = 0.55; starData[o + 5] = 1.0; // violet
    }
    starData[o + 6] = 1.0;                              // a
    starData[o + 7] = Math.random() * Math.PI * 2;      // twinkle phase
  }
  const starInstanceBuf = gl.createBuffer();
  if (!starInstanceBuf) throw new Error('failed to create star instance buffer');
  gl.bindBuffer(gl.ARRAY_BUFFER, starInstanceBuf);
  gl.bufferData(gl.ARRAY_BUFFER, starData, gl.STATIC_DRAW);
  const starVAO = gl.createVertexArray();
  if (!starVAO) throw new Error('failed to create star VAO');
  gl.bindVertexArray(starVAO);
  twgl.setBuffersAndAttributes(gl, starProg, particleQuad);
  setupStarInstanceAttribs(gl, starProg, starInstanceBuf);
  gl.bindVertexArray(null);

  glState = {
    gl, fullQuadProg, gridProg, entityProg, particleProg, splatProg, solidProg, starProg,
    fullQuadBuf, gridBuf, entityBuf, entityInstanceBuf, entityVAO,
    particleInstanceBuf, particleVAO, splatInstanceBuf, splatVAO,
    starInstanceBuf, starVAO, starCount: STAR_COUNT, solidBuf,
    staticTex, staticTexKey: '',
  };
  return glState;
}

const STAR_INSTANCE_STRIDE_F = 8; // ndc(2) + size(1) + color(4) + phase(1)

function setupStarInstanceAttribs(
  gl: WebGL2RenderingContext,
  prog: twgl.ProgramInfo,
  buf: WebGLBuffer,
): void {
  const stride = STAR_INSTANCE_STRIDE_F * 4;
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  const set = (name: string, size: number, offF: number) => {
    const loc = gl.getAttribLocation(prog.program, name);
    if (loc < 0) return;
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, offF * 4);
    gl.vertexAttribDivisor(loc, 1);
  };
  set('a_starPos', 3, 0);
  set('a_starColor', 4, 3);
  set('a_phase', 1, 7);
}

const PARTICLE_INSTANCE_STRIDE_F = 10; // head(2) + tail(2) + width(1) + color(4) + age01(1)

function setupParticleInstanceAttribs(
  gl: WebGL2RenderingContext,
  prog: twgl.ProgramInfo,
  buf: WebGLBuffer,
): void {
  const stride = PARTICLE_INSTANCE_STRIDE_F * 4;
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  const set = (name: string, size: number, offF: number) => {
    const loc = gl.getAttribLocation(prog.program, name);
    if (loc < 0) return;
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, offF * 4);
    gl.vertexAttribDivisor(loc, 1);
  };
  set('a_head', 2, 0);
  set('a_tail', 2, 2);
  set('a_width', 1, 4);
  set('a_color', 4, 5);
  set('a_age01', 1, 9);
}

const SPLAT_INSTANCE_STRIDE_F = 10; // screen(2) + radius(2) + color(4) + age01(1) + type(1)

function setupSplatInstanceAttribs(
  gl: WebGL2RenderingContext,
  prog: twgl.ProgramInfo,
  buf: WebGLBuffer,
): void {
  const stride = SPLAT_INSTANCE_STRIDE_F * 4;
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  const set = (name: string, size: number, offF: number) => {
    const loc = gl.getAttribLocation(prog.program, name);
    if (loc < 0) return;
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, offF * 4);
    gl.vertexAttribDivisor(loc, 1);
  };
  set('a_screen', 2, 0);
  set('a_radius', 2, 2);
  set('a_color', 4, 4);
  set('a_age01', 1, 8);
  set('a_type', 1, 9);
}

const ENTITY_INSTANCE_STRIDE_F = 11; // floats per instance

function setupEntityInstanceAttribs(
  gl: WebGL2RenderingContext,
  prog: twgl.ProgramInfo,
  buf: WebGLBuffer,
): void {
  const stride = ENTITY_INSTANCE_STRIDE_F * 4; // bytes
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  const setup = (name: string, size: number, offsetF: number) => {
    const info = (prog.attribSetters as Record<string, unknown>)[name];
    if (!info) return; // attrib not in program (compiler stripped it)
    // We need the location; twgl stores it in setter closures, so use raw API.
    const loc = gl.getAttribLocation(prog.program, name);
    if (loc < 0) return;
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, offsetF * 4);
    gl.vertexAttribDivisor(loc, 1); // per-instance
  };
  setup('a_instancePosSize', 3, 0);
  setup('a_instanceAspect', 2, 3);
  setup('a_instanceColor', 4, 5);
  setup('a_instanceBodyFrac', 1, 9);
  setup('a_instanceShape', 1, 10);
}

function ensureStaticTexUploaded(s: GLState, canvas: HTMLCanvasElement, role: Role): void {
  // Mirror the Canvas-2D static cache key so mid-match config changes
  // (e.g., switching to Pong) propagate to the GPU texture.
  const cfg = getCurrentConfig();
  const key = `${canvas.width}x${canvas.height}:${role}:${cfg.goalHalfHeight}:${cfg.pocketDepth}`;
  if (s.staticTexKey === key) return;
  const offscreen = getStaticLayer(canvas, role);
  const gl = s.gl;
  gl.bindTexture(gl.TEXTURE_2D, s.staticTex);
  gl.texImage2D(
    gl.TEXTURE_2D, 0, gl.RGBA,
    gl.RGBA, gl.UNSIGNED_BYTE,
    offscreen,
  );
  s.staticTexKey = key;
}

// ─── Per-frame: collect data from canvas-renderer state ───────────────────
// Canvas-2D module is the authority on simulation state (effects, grid
// forces, sparks). We import getters from it; both renderers call the
// shared tickScene() to age that state each frame.

// ─── Render entry ─────────────────────────────────────────────────────────

export function render(
  canvas: HTMLCanvasElement,
  state: GameState,
  role: Role,
  now: number,
): void {
  const s = glState ?? initGL(canvas);
  const gl = s.gl;

  // Shared scene state housekeeping (ages effects, spawns sparks, etc).
  tickScene(state, now);
  const shake = getShakeOffset(now);

  ensureStaticTexUploaded(s, canvas, role);

  gl.viewport(0, 0, canvas.width, canvas.height);
  const [br, bg, bb] = hexToRgba(PALETTE.bg);
  gl.clearColor(br, bg, bb, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);

  drawStarfield(s, canvas, now);
  drawStaticFullQuad(s);
  drawGrid(s, canvas, role, now, shake);
  drawGoalAmbient(s, canvas, role, now, shake);
  drawGoalCelebration(s, canvas, role, now, shake);
  drawWallFlashes(s, canvas, role, now, shake);
  drawEntities(s, canvas, state, role, shake);
  drawImpactRings(s, canvas, role, now, shake);
  drawParticles(s, canvas, role, now, shake);
}

function drawStaticFullQuad(s: GLState): void {
  const gl = s.gl;
  gl.useProgram(s.fullQuadProg.program);
  twgl.setBuffersAndAttributes(gl, s.fullQuadProg, s.fullQuadBuf);
  twgl.setUniforms(s.fullQuadProg, { u_tex: s.staticTex });
  // Static cache is now transparent outside the rink so the starfield
  // shows through — use normal alpha blending.
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  twgl.drawBufferInfo(gl, s.fullQuadBuf);
}

function drawStarfield(s: GLState, canvas: HTMLCanvasElement, now: number): void {
  const gl = s.gl;
  gl.useProgram(s.starProg.program);
  gl.bindVertexArray(s.starVAO);
  twgl.setUniforms(s.starProg, {
    u_resolution: [canvas.width, canvas.height],
    u_time: now / 1000,
  });
  gl.enable(gl.BLEND);
  // Additive — stars accumulate brightness if they happen to overlap.
  gl.blendFunc(gl.ONE, gl.ONE);
  gl.drawElementsInstanced(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0, s.starCount);
  gl.bindVertexArray(null);
}

function drawGrid(
  s: GLState, canvas: HTMLCanvasElement, role: Role, now: number,
  shake: { dx: number; dy: number },
): void {
  const gl = s.gl;
  const v = computeView(canvas, role);

  // Pack active grid forces into uniform arrays.
  const forces = getGridForceSnapshot();
  const count = Math.min(forces.length, MAX_FORCES);
  const fArr = new Float32Array(MAX_FORCES * 4);
  const ageTermArr = new Float32Array(MAX_FORCES);
  for (let i = 0; i < count; i++) {
    const f = forces[i]!;
    const age = (now - f.t0) / f.duration;
    if (age < 0 || age >= 1) {
      // Effectively zero-out this entry.
      fArr[i * 4 + 2] = 0;
      fArr[i * 4 + 3] = 1;
      continue;
    }
    const ageFalloff = (1 - age) * (1 - age);
    fArr[i * 4 + 0] = f.wx;
    fArr[i * 4 + 1] = f.wy;
    fArr[i * 4 + 2] = f.magnitude * ageFalloff;
    fArr[i * 4 + 3] = f.radius;
    ageTermArr[i] = age * Math.PI * 3;
  }

  gl.useProgram(s.gridProg.program);
  twgl.setBuffersAndAttributes(gl, s.gridProg, s.gridBuf);
  twgl.setUniforms(s.gridProg, {
    u_resolution: [canvas.width, canvas.height],
    u_shake: [shake.dx, shake.dy],
    u_rinkW: RINK.width,
    u_rinkH: RINK.height,
    u_role: role === 'host' ? 0 : 1,
    u_view: [v.nearY, v.farY, v.nearHalfWidth, v.farHalfWidth],
    u_persp: [v.a, v.horizonY, v.screenYScale],
    u_forceCount: count,
    u_forces: fArr,
    u_forceAgeTerm: ageTermArr,
    u_color: [0.133, 0.906, 1.0, 0.75], // cyan
  });

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  // Lines via gl.LINES on the indexed pair list.
  gl.drawElements(gl.LINES, s.gridBuf.numElements, gl.UNSIGNED_SHORT, 0);
}

function drawEntities(
  s: GLState,
  canvas: HTMLCanvasElement,
  state: GameState,
  role: Role,
  shake: { dx: number; dy: number },
): void {
  const gl = s.gl;
  const v = computeView(canvas, role);

  // Hide the puck during goal phase — it just exploded.
  const puckGone = state.phase === 'goal-host' || state.phase === 'goal-guest';
  const entityCount = puckGone ? 2 : 3;
  const data = new Float32Array(entityCount * ENTITY_INSTANCE_STRIDE_F);
  // Write an entity instance. worldHalfLat / worldHalfDepth are the
  // body half-extents in WORLD units along the lateral (world-y → screen-x)
  // and depth (world-x → screen-y) axes. Circle entities pass the same
  // value for both; wide rectangular paddles pass big lateral / small depth.
  const writeEntity = (
    idx: number, wx: number, wy: number,
    worldHalfLat: number, worldHalfDepth: number,
    color: [number, number, number, number],
    bodyFrac: number,
    shape = 0,   // 0 = circle SDF, 1 = rectangle SDF
  ) => {
    const p = project(wx, wy, v);
    // Halo extends 1.8× past the body in each axis. Both axes use
    // latScale so circle entities render circular (not depth-elongated
    // ellipses).
    const haloRx = worldHalfLat * p.latScale * 1.8;
    const haloRy = worldHalfDepth * p.latScale * 1.8 * ENTITY_FORESHORTEN;
    const o = idx * ENTITY_INSTANCE_STRIDE_F;
    data[o + 0] = p.x;
    data[o + 1] = p.y;
    // posSize.z is the "base" halo radius; aspect skews it per-axis.
    data[o + 2] = haloRx;
    data[o + 3] = 1.0;
    data[o + 4] = haloRy / haloRx;
    data[o + 5] = color[0];
    data[o + 6] = color[1];
    data[o + 7] = color[2];
    data[o + 8] = color[3];
    data[o + 9] = bodyFrac;
    data[o + 10] = shape;
  };
  // Paddle is wide in lateral (world-y) and thin in depth (world-x) for
  // Pong; equal for circle paddles. When the ratio is substantial, the
  // SDF switches to a sharp-cornered rectangle.
  const paddleAspect = state.config.paddleHalfHeight / state.config.paddleRadius;
  const paddleShape = paddleAspect > 1.4 ? 1 : 0;
  writeEntity(0,
    state.host.pos.x, state.host.pos.y,
    state.config.paddleHalfHeight, state.config.paddleRadius,
    hexToRgba(PALETTE.host), 1.0 / 1.8, paddleShape);
  writeEntity(1,
    state.guest.pos.x, state.guest.pos.y,
    state.config.paddleHalfHeight, state.config.paddleRadius,
    hexToRgba(PALETTE.guest), 1.0 / 1.8, paddleShape);
  if (!puckGone) {
    // Puck halo grows with current speed — fast puck visibly radiates.
    // bodyFrac shrinks proportionally so the body stays at the collision
    // radius (only the halo expands).
    const speedT = Math.min(1, getCurrentPuckSpeed() / 14);
    const haloBoost = 1 + speedT * 0.55;
    const radius = state.config.puckRadius * haloBoost;
    const bodyFrac = (1.0 / 1.8) / haloBoost;
    writeEntity(2, state.puck.pos.x, state.puck.pos.y, radius, radius, hexToRgba(PALETTE.puck), bodyFrac);
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, s.entityInstanceBuf);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);

  gl.useProgram(s.entityProg.program);
  gl.bindVertexArray(s.entityVAO);
  twgl.setUniforms(s.entityProg, {
    u_resolution: [canvas.width, canvas.height],
    u_shake: [shake.dx, shake.dy],
  });

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
  gl.drawElementsInstanced(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0, entityCount);
  gl.bindVertexArray(null);
}

// ─── Effect rendering (Stage 2) ─────────────────────────────────────────

function projectScreen(wx: number, wy: number, v: View): { x: number; y: number; latScale: number } {
  return project(wx, wy, v);
}

function drawParticles(
  s: GLState, canvas: HTMLCanvasElement, role: Role, now: number,
  shake: { dx: number; dy: number },
): void {
  const gl = s.gl;
  const v = computeView(canvas, role);
  const effs = getEffectsSnapshot();
  // Two passes per particle (we draw it as ONE quad). Count how many
  // particles are alive and within the frame.
  let count = 0;
  for (const e of effs) if (e.kind === 'particle') count++;
  if (count === 0) return;

  const data = new Float32Array(count * PARTICLE_INSTANCE_STRIDE_F);
  let i = 0;
  for (const e of effs) {
    if (e.kind !== 'particle') continue;
    const ageMs = now - e.t0;
    const t = ageMs / e.duration;
    if (t < 0 || t > 1) continue;
    // Same motion math as the Canvas 2D version, so visuals match.
    const ageTicks = ageMs / 16.67;
    const decay = Math.pow(0.985, ageTicks);
    const wx = e.wx + e.vx * ageTicks * decay;
    const wy = e.wy + e.vy * ageTicks * decay;
    const vxNow = e.vx * decay;
    const vyNow = e.vy * decay;
    const speed = Math.hypot(vxNow, vyNow);
    const streakLen = Math.min(20, 3 + speed * 1.4);
    const invSpeed = 1 / Math.max(speed, 0.01);
    const wxTail = wx - vxNow * invSpeed * streakLen;
    const wyTail = wy - vyNow * invSpeed * streakLen;
    const head = projectScreen(wx, wy, v);
    const tail = projectScreen(wxTail, wyTail, v);
    const width = Math.max(1.0, e.size * head.latScale * 0.45);
    const [r, g, b, a] = hexToRgba(e.color);
    const o = i * PARTICLE_INSTANCE_STRIDE_F;
    data[o + 0] = head.x;
    data[o + 1] = head.y;
    data[o + 2] = tail.x;
    data[o + 3] = tail.y;
    data[o + 4] = width;
    data[o + 5] = r;
    data[o + 6] = g;
    data[o + 7] = b;
    data[o + 8] = a;
    data[o + 9] = t;
    i++;
  }
  if (i === 0) return;

  gl.bindBuffer(gl.ARRAY_BUFFER, s.particleInstanceBuf);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);

  gl.useProgram(s.particleProg.program);
  gl.bindVertexArray(s.particleVAO);
  twgl.setUniforms(s.particleProg, {
    u_resolution: [canvas.width, canvas.height],
    u_shake: [shake.dx, shake.dy],
  });
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE); // additive — many overlapping streaks brighten
  gl.drawElementsInstanced(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0, i);
  gl.bindVertexArray(null);
}

function drawSplats(
  s: GLState, canvas: HTMLCanvasElement, role: Role, now: number,
  shake: { dx: number; dy: number },
  filter: (e: Effect) => boolean,
  toInstance: (e: Effect, v: View, write: (
    screen: { x: number; y: number; latScale: number },
    radius: number,
    color: [number, number, number, number],
    age01: number,
    type: 0 | 1,
  ) => void) => void,
): void {
  const gl = s.gl;
  const v = computeView(canvas, role);
  const effs = getEffectsSnapshot();
  let count = 0;
  for (const e of effs) if (filter(e)) count++;
  if (count === 0) return;

  const data = new Float32Array(count * SPLAT_INSTANCE_STRIDE_F);
  let i = 0;
  const write = (
    screen: { x: number; y: number; latScale: number },
    radius: number,
    color: [number, number, number, number],
    age01: number,
    type: 0 | 1,
  ) => {
    const o = i * SPLAT_INSTANCE_STRIDE_F;
    data[o + 0] = screen.x;
    data[o + 1] = screen.y;
    data[o + 2] = radius;
    data[o + 3] = radius * ENTITY_FORESHORTEN;
    data[o + 4] = color[0];
    data[o + 5] = color[1];
    data[o + 6] = color[2];
    data[o + 7] = color[3];
    data[o + 8] = age01;
    data[o + 9] = type;
    i++;
  };
  for (const e of effs) {
    if (!filter(e)) continue;
    toInstance(e, v, write);
  }
  if (i === 0) return;

  gl.bindBuffer(gl.ARRAY_BUFFER, s.splatInstanceBuf);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);

  gl.useProgram(s.splatProg.program);
  gl.bindVertexArray(s.splatVAO);
  twgl.setUniforms(s.splatProg, {
    u_resolution: [canvas.width, canvas.height],
    u_shake: [shake.dx, shake.dy],
  });
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE);
  gl.drawElementsInstanced(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0, i);
  gl.bindVertexArray(null);
}

function drawWallFlashes(
  s: GLState, canvas: HTMLCanvasElement, role: Role, now: number,
  shake: { dx: number; dy: number },
): void {
  drawSplats(s, canvas, role, now, shake,
    (e) => e.kind === 'wallflash',
    (e, v, write) => {
      if (e.kind !== 'wallflash') return;
      const t = (now - e.t0) / e.duration;
      if (t < 0 || t > 1) return;
      const sp = projectScreen(e.wx, e.wy, v);
      const r = (getCurrentConfig().puckRadius * 1.4) * sp.latScale * (1 + t * 0.6);
      write(sp, r * 2, [1.0, 0.969, 0.659, 0.9], t, 0);
    });
}

function drawImpactRings(
  s: GLState, canvas: HTMLCanvasElement, role: Role, now: number,
  shake: { dx: number; dy: number },
): void {
  drawSplats(s, canvas, role, now, shake,
    (e) => e.kind === 'impact',
    (e, v, write) => {
      if (e.kind !== 'impact') return;
      const t = (now - e.t0) / e.duration;
      if (t < 0 || t > 1) return;
      const sp = projectScreen(e.wx, e.wy, v);
      const cfg = getCurrentConfig();
      const baseR = (cfg.paddleRadius + cfg.puckRadius) * sp.latScale;
      const r = baseR * (0.6 + t * 1.3);
      write(sp, r, hexToRgba(e.color), t, 1);
    });
}

/**
 * Always-on subtle pulse over each goal pocket. Pulses are out of
 * phase so the two goals breathe independently. Additive blending on
 * top of the cached pocket colors.
 */
function drawGoalAmbient(
  s: GLState, canvas: HTMLCanvasElement, role: Role, now: number,
  shake: { dx: number; dy: number },
): void {
  const v = computeView(canvas, role);
  const yLow = RINK.height / 2 - RINK.goalHalfHeight;
  const yHigh = RINK.height / 2 + RINK.goalHalfHeight;
  const pulseHost = 0.16 + 0.14 * Math.sin(now / 700);
  const pulseGuest = 0.16 + 0.14 * Math.sin(now / 700 + Math.PI * 0.7);
  drawAmbientPocket(s, v, canvas, shake, 0, -36, yLow, yHigh, [1.0, 0.169, 0.839, pulseHost]);
  drawAmbientPocket(s, v, canvas, shake, RINK.width, RINK.width + 36, yLow, yHigh, [0.133, 0.906, 1.0, pulseGuest]);
}

function drawAmbientPocket(
  s: GLState, v: View, canvas: HTMLCanvasElement,
  shake: { dx: number; dy: number },
  inWx: number, outWx: number, yLow: number, yHigh: number,
  color: [number, number, number, number],
): void {
  const gl = s.gl;
  const a = project(inWx, yLow, v);
  const b = project(inWx, yHigh, v);
  const c = project(outWx, yHigh, v);
  const d = project(outWx, yLow, v);
  const positions = new Float32Array([
    a.x, a.y, b.x, b.y, d.x, d.y,
    b.x, b.y, c.x, c.y, d.x, d.y,
  ]);
  gl.bindBuffer(gl.ARRAY_BUFFER, s.solidBuf);
  gl.bufferData(gl.ARRAY_BUFFER, positions, gl.DYNAMIC_DRAW);
  gl.useProgram(s.solidProg.program);
  const loc = gl.getAttribLocation(s.solidProg.program, 'a_position');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  twgl.setUniforms(s.solidProg, {
    u_resolution: [canvas.width, canvas.height],
    u_shake: [shake.dx, shake.dy],
    u_color: color,
  });
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

function drawGoalCelebration(
  s: GLState, canvas: HTMLCanvasElement, role: Role, now: number,
  shake: { dx: number; dy: number },
): void {
  const gl = s.gl;
  const v = computeView(canvas, role);
  const effs = getEffectsSnapshot();
  for (const e of effs) {
    if (e.kind !== 'goal') continue;
    const t = (now - e.t0) / e.duration;
    if (t < 0 || t > 1) continue;
    const pulse = Math.max(0, Math.cos(t * Math.PI * 3) * Math.exp(-t * 1.5));
    const color = e.scorer === 'host' ? hexToRgba('#ff2bd6') : hexToRgba('#22e7ff');
    const inWx = e.scorer === 'host' ? RINK.width : 0;
    const outWx = e.scorer === 'host' ? RINK.width + 36 : -36;
    const yLow = RINK.height / 2 - RINK.goalHalfHeight;
    const yHigh = RINK.height / 2 + RINK.goalHalfHeight;
    const a = projectScreen(inWx, yLow, v);
    const b = projectScreen(inWx, yHigh, v);
    const c = projectScreen(outWx, yHigh, v);
    const d = projectScreen(outWx, yLow, v);
    const positions = new Float32Array([
      a.x, a.y, b.x, b.y, d.x, d.y,
      b.x, b.y, c.x, c.y, d.x, d.y,
    ]);
    gl.bindBuffer(gl.ARRAY_BUFFER, s.solidBuf);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.DYNAMIC_DRAW);
    gl.useProgram(s.solidProg.program);
    const loc = gl.getAttribLocation(s.solidProg.program, 'a_position');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const alpha = (0.6 + 0.4 * pulse);
    twgl.setUniforms(s.solidProg, {
      u_resolution: [canvas.width, canvas.height],
      u_shake: [shake.dx, shake.dy],
      u_color: [color[0], color[1], color[2], alpha],
    });
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }
}
